import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { DreSection, DreTotals } from "@/hooks/use-dre";

interface DreTableProps {
  sections: DreSection[];
  totals: DreTotals;
}

type RowType =
  | { kind: "section_header"; label: string; total: number; isExpense: boolean }
  | { kind: "category"; name: string; amount: number; color: string; icon: string }
  | { kind: "separator" }
  | { kind: "total"; label: string; value: number; highlight?: boolean };

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);

const formatPercent = (value: number, base: number): string => {
  if (base === 0) return "-";
  return `${((value / base) * 100).toFixed(1)}%`;
};

const valueColorClass = (value: number): string => {
  if (value > 0) return "text-emerald-600 dark:text-emerald-400";
  if (value < 0) return "text-red-600 dark:text-red-400";
  return "text-muted-foreground";
};

/**
 * Finds sections matching the given keys from the sections array.
 * Returns them in the order of the keys provided.
 */
function findSections(sections: DreSection[], keys: string[]): DreSection[] {
  return keys
    .map((key) => sections.find((s) => s.section === key))
    .filter(Boolean) as DreSection[];
}

/**
 * Builds DRE rows for a group of sections, including section headers
 * and their individual category detail rows.
 */
function buildSectionRows(
  matchedSections: DreSection[],
  isExpense: boolean
): RowType[] {
  const rows: RowType[] = [];
  for (const section of matchedSections) {
    rows.push({
      kind: "section_header",
      label: isExpense ? `(-) ${section.label}` : section.label,
      total: section.total,
      isExpense,
    });
    for (const cat of section.categories) {
      rows.push({
        kind: "category",
        name: cat.category_name,
        amount: cat.amount,
        color: cat.category_color,
        icon: cat.category_icon,
      });
    }
  }
  return rows;
}

export function DreTable({ sections, totals }: DreTableProps) {
  if (sections.length === 0) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        Nenhum dado disponivel para o periodo selecionado
      </div>
    );
  }

  const receitaBruta = totals.receita_bruta;

  // Build all rows in classical DRE order
  const rows: RowType[] = [];

  // 1. Revenue sections
  const revenueSections = findSections(sections, [
    "receita_operacional",
    "outras_receitas",
  ]);
  rows.push(...buildSectionRows(revenueSections, false));

  // Separator + RECEITA BRUTA
  rows.push({ kind: "separator" });
  rows.push({
    kind: "total",
    label: "RECEITA BRUTA",
    value: totals.receita_bruta,
  });

  // 2. Cost sections
  const costSections = findSections(sections, ["custo_operacional"]);
  rows.push(...buildSectionRows(costSections, true));

  // Separator + LUCRO BRUTO
  rows.push({ kind: "separator" });
  rows.push({
    kind: "total",
    label: "LUCRO BRUTO",
    value: totals.lucro_bruto,
  });

  // 3. OpEx sections
  const opexSections = findSections(sections, [
    "despesa_administrativa",
    "despesa_comercial",
    "despesa_pessoal",
    "outras_despesas",
  ]);
  rows.push(...buildSectionRows(opexSections, true));

  // Separator + RESULTADO OPERACIONAL
  rows.push({ kind: "separator" });
  rows.push({
    kind: "total",
    label: "RESULTADO OPERACIONAL",
    value: totals.resultado_operacional,
  });

  // 4. Financial sections
  const financialExpenseSections = findSections(sections, [
    "despesa_financeira",
  ]);
  rows.push(...buildSectionRows(financialExpenseSections, true));

  const financialIncomeSections = findSections(sections, [
    "receita_financeira",
  ]);
  // Financial income uses (+) prefix
  for (const section of financialIncomeSections) {
    rows.push({
      kind: "section_header",
      label: `(+) ${section.label}`,
      total: section.total,
      isExpense: false,
    });
    for (const cat of section.categories) {
      rows.push({
        kind: "category",
        name: cat.category_name,
        amount: cat.amount,
        color: cat.category_color,
        icon: cat.category_icon,
      });
    }
  }

  // Separator + RESULTADO LIQUIDO
  rows.push({ kind: "separator" });
  rows.push({
    kind: "total",
    label: "RESULTADO LIQUIDO",
    value: totals.resultado_liquido,
    highlight: true,
  });

  return (
    <div className="rounded-lg border bg-card overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/70">
            <TableHead className="w-[60%] sm:w-[50%] font-bold">Descricao</TableHead>
            <TableHead className="text-right font-bold">Valor</TableHead>
            <TableHead className="text-right font-bold w-[100px] hidden sm:table-cell">
              % Receita
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, index) => {
            if (row.kind === "separator") {
              return (
                <TableRow key={`sep-${index}`} className="hover:bg-transparent">
                  <TableCell
                    colSpan={3}
                    className="p-0 border-t-2 border-primary"
                  />
                </TableRow>
              );
            }

            if (row.kind === "total") {
              const isPositive = row.value >= 0;
              return (
                <TableRow
                  key={`total-${index}`}
                  className={`bg-muted/30 hover:bg-muted/50 ${
                    row.highlight ? "border-t-2 border-b-2 border-primary" : ""
                  }`}
                >
                  <TableCell className="font-bold text-base py-3">
                    = {row.label}
                  </TableCell>
                  <TableCell
                    className={`text-right font-bold text-base py-3 ${
                      row.highlight
                        ? valueColorClass(row.value)
                        : isPositive
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-red-600 dark:text-red-400"
                    }`}
                  >
                    {formatCurrency(row.value)}
                  </TableCell>
                  <TableCell className="text-right font-bold text-sm py-3 text-muted-foreground hidden sm:table-cell">
                    {formatPercent(row.value, receitaBruta)}
                  </TableCell>
                </TableRow>
              );
            }

            if (row.kind === "section_header") {
              return (
                <TableRow
                  key={`section-${index}`}
                  className="bg-muted/50 hover:bg-muted/60"
                >
                  <TableCell className="font-semibold py-2.5">
                    {row.label}
                  </TableCell>
                  <TableCell
                    className={`text-right font-semibold py-2.5 ${
                      row.isExpense
                        ? "text-red-600 dark:text-red-400"
                        : "text-emerald-600 dark:text-emerald-400"
                    }`}
                  >
                    {formatCurrency(row.total)}
                  </TableCell>
                  <TableCell className="text-right text-sm py-2.5 text-muted-foreground hidden sm:table-cell">
                    {formatPercent(row.total, receitaBruta)}
                  </TableCell>
                </TableRow>
              );
            }

            // category row
            return (
              <TableRow key={`cat-${index}`} className="hover:bg-muted/30">
                <TableCell className="pl-4 sm:pl-8 text-sm py-2">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ backgroundColor: row.color }}
                    />
                    {row.name}
                  </div>
                </TableCell>
                <TableCell className="text-right text-sm py-2">
                  {formatCurrency(row.amount)}
                </TableCell>
                <TableCell className="text-right text-sm py-2 text-muted-foreground hidden sm:table-cell">
                  {formatPercent(row.amount, receitaBruta)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
